/**
 * CRM workspace tabs — pure state-management helpers (Task #71, Jun 9 2026).
 *
 * Manny's P1 requirement: sales reps and PEMs need to keep 5-10 contacts
 * open at once, switch between them instantly, and never lose draft work
 * when an inbound call interrupts.
 *
 * Pure functions, no React imports. Drives the multi-tab state machine
 * in CRMPage. Unit-testable without rendering.
 *
 * State shape (always-flat, easy to localStorage round-trip):
 *
 *   {
 *     tabs:     [{ id, name, lead_temperature?, pipeline_stage?, opened_at }],
 *     activeId: number | null
 *   }
 *
 * Tabs are ordered by open time (oldest left, newest right). activeId
 * is the contact id of the tab currently shown; null when no tabs are
 * open and the user is back on the master list.
 */
"use strict";

// Task #94 (Jun 9 2026): converted from CJS module.exports to ESM
// named exports. The previous CJS form tripped webpack 5.106's
// CommonJsPlugin runtime check ("ES Modules may not assign
// module.exports or exports.*") because webpack tagged this file as
// ESM despite the absence of any import/export statements, then refused
// the module.exports assignment. The bundle worked fine in earlier
// webpack 5 builds — something between 5.10x and 5.106 made the
// detection stricter. Switching to named ESM exports removes the
// ambiguity entirely.
//
// Unit tests under tests/unit/workspaceTabs.test.js still `require()`
// this file. jest.config.js now applies a narrow babel-jest transform
// to src/ files in the unit project (CRA-style preset-env targeting
// node=current) so the `export` keywords compile to module.exports
// before Jest sees them. Test code itself stays untransformed.

export const MAX_TABS = 12;  // Generous ceiling — Manny's spec said 5-10,
                      // with headroom for unusual days. Above this we'd
                      // hurt both render performance and rep cognitive
                      // load.

const LS_KEY_PREFIX = "crm.workspace.tabs.v1.";  // per-user key suffix
                                                 // applied by caller

/** @returns {{tabs: Array, activeId: number|null}} */
export function emptyState() {
  return { tabs: [], activeId: null };
}

/**
 * Open a tab for the given contact. If already open, activate it
 * without duplicating. Returns a new state (immutable).
 *
 * @param {object} state    current workspace state
 * @param {object} contact  { id, name, lead_temperature?, pipeline_stage? }
 */
export function openTab(state, contact) {
  if (!state) state = emptyState();
  if (!contact || contact.id == null) return state;
  const id = Number(contact.id);
  if (!Number.isFinite(id)) return state;

  // Already open? Just activate.
  if (state.tabs.find(t => t.id === id)) {
    return { tabs: state.tabs, activeId: id };
  }
  // At capacity? Drop the OLDEST inactive tab. Never drop the active
  // tab — the rep is mid-conversation in it.
  let tabs = state.tabs;
  if (tabs.length >= MAX_TABS) {
    const droppable = tabs.find(t => t.id !== state.activeId);
    if (droppable) tabs = tabs.filter(t => t.id !== droppable.id);
  }
  const newTab = {
    id,
    name: contact.name || `${contact.first_name || ""} ${contact.last_name || ""}`.trim() || `Contact ${id}`,
    lead_temperature: contact.lead_temperature || null,
    pipeline_stage: contact.pipeline_stage || null,
    opened_at: Date.now(),
  };
  return { tabs: [...tabs, newTab], activeId: id };
}

/**
 * Close a tab. If it was active, activate the tab immediately to its
 * left (the rep was probably working through them in opened-at order).
 * If no tabs remain, activeId becomes null.
 */
export function closeTab(state, id) {
  if (!state) return emptyState();
  const targetId = Number(id);
  const idx = state.tabs.findIndex(t => t.id === targetId);
  if (idx === -1) return state;

  const tabs = state.tabs.filter(t => t.id !== targetId);
  let activeId = state.activeId;
  if (state.activeId === targetId) {
    // Active tab being closed - activate the one to its left, or to
    // its right if it was first, or null if no tabs remain.
    if (tabs.length === 0) {
      activeId = null;
    } else {
      const newIdx = Math.max(0, idx - 1);
      activeId = tabs[Math.min(newIdx, tabs.length - 1)].id;
    }
  }
  return { tabs, activeId };
}

export function activateTab(state, id) {
  if (!state) return emptyState();
  const target = Number(id);
  if (!state.tabs.find(t => t.id === target)) return state;
  return { tabs: state.tabs, activeId: target };
}

/**
 * Step OFF the active record WITHOUT closing it — Card P111 (B1).
 *
 * ⛔⛔ WHY THIS IS NOT `closeTab`, AND WHY IT IS NOT `activateTab(null)`.
 *
 * CRMPage renders the contact detail view whenever `activeId` is set and
 * suppresses every section tab body while it is — so changing the CRM's top
 * nav with a record open changed nothing on screen. The cure is to put the
 * workspace down, not to throw it away: the rep clicked Pipeline, they did not
 * ask to lose the three contacts they have open. `closeTab` would discard one;
 * `activateTab` CANNOT express this at all, because it refuses any id that is
 * not an open tab (including null) and returns the state unchanged.
 *
 * So the result is the one shape this module could not previously produce:
 * tabs open, none active. WorkspaceTabBar already renders exactly that — it
 * keys off `tabs.length` and simply draws no tab as selected — so the rep sees
 * their records sitting there and one click puts them back.
 *
 * ⚠ A state with NO tabs is returned unchanged rather than rebuilt, so this is
 * a no-op on the master list instead of a pointless new object identity.
 */
export function deactivate(state) {
  if (!state) return emptyState();
  if (state.activeId == null) return state;
  return { tabs: state.tabs, activeId: null };
}

/** Close every tab. */
export function closeAll() {
  return emptyState();
}

/**
 * Patch a single tab's metadata in place (e.g. lead temperature changed
 * on the server side after the rep updated it). Used to keep the tab
 * label fresh without forcing a re-fetch.
 */
export function patchTab(state, id, patch) {
  if (!state) return emptyState();
  const target = Number(id);
  return {
    ...state,
    tabs: state.tabs.map(t => t.id === target ? { ...t, ...patch } : t),
  };
}

// ─── localStorage persistence ─────────────────────────────────────────
export function localStorageKey(userId) {
  return LS_KEY_PREFIX + (userId == null ? "anon" : String(userId));
}

export function persistToLocalStorage(state, userId, storage) {
  const target = storage || (typeof window !== "undefined" ? window.localStorage : null);
  if (!target) return false;
  try {
    target.setItem(localStorageKey(userId), JSON.stringify(state || emptyState()));
    return true;
  } catch (_) {
    return false;
  }
}

export function loadFromLocalStorage(userId, storage) {
  const target = storage || (typeof window !== "undefined" ? window.localStorage : null);
  if (!target) return emptyState();
  try {
    const raw = target.getItem(localStorageKey(userId));
    if (!raw) return emptyState();
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.tabs)) return emptyState();
    // Defensive validation — drop any malformed tab entries
    const tabs = parsed.tabs
      .filter(t => t && typeof t === "object" && Number.isFinite(Number(t.id)))
      .map(t => ({
        id: Number(t.id),
        name: String(t.name || `Contact ${t.id}`),
        lead_temperature: t.lead_temperature || null,
        pipeline_stage: t.pipeline_stage || null,
        opened_at: Number(t.opened_at) || Date.now(),
      }))
      .slice(0, MAX_TABS);
    /*
     * ⛔⛔ AN EXPLICIT `null` IS A DECISION; AN ABSENT KEY IS A GAP — Card P111.
     *
     * This used to read: "no usable activeId ⇒ activate tabs[0]". That was
     * correct while "tabs open, none active" was UNREACHABLE — openTab always
     * activates, closeTab picks a neighbour, activateTab refuses a non-tab, so
     * the only way to have no active record was to have no tabs at all, and
     * the fallback could only ever fire on a corrupt or legacy record.
     *
     * `deactivate()` makes that shape reachable, and the old fallback would
     * have quietly reversed it ON THE NEXT PAGE LOAD: the rep steps off a
     * record onto Pipeline, refreshes, and the contact overlay is back with
     * every tab body suppressed again — the very bug this card fixes,
     * returning on a reload, with the in-session behaviour still looking
     * correct. A green screen and a defect that comes back with F5.
     *
     * So the two cases are now told apart by the KEY, not by the value.
     * JSON.stringify writes `"activeId":null` for an explicit null and omits
     * the key entirely when it is undefined, so the distinction survives the
     * round trip. A record written by any previous version always carries a
     * real id or has no tabs, and is unaffected either way.
     */
    const declaredNoActive =
      Object.prototype.hasOwnProperty.call(parsed, "activeId") && parsed.activeId === null;
    const activeId = parsed.activeId != null
      && tabs.some(t => t.id === Number(parsed.activeId))
        ? Number(parsed.activeId)
        : (declaredNoActive ? null : (tabs[0] ? tabs[0].id : null));
    return { tabs, activeId };
  } catch (_) {
    return emptyState();
  }
}

/**
 * Read the persisted CRM SECTION tab (NAV-4, issue #380).
 *
 * ⚠ TWO DIFFERENT THINGS ARE CALLED "TAB" IN THIS FILE'S VICINITY, and
 * conflating them is the trap. The `tabs` array above holds the open-CONTACT
 * tabs of the workspace (Task #71). The SECTION tab is which CRM area is
 * showing — My Day, Pipeline, Contacts. They are unrelated concepts, which is
 * why this is a separate `sectionTab` field and NOT an entry in `tabs`.
 *
 * ── WHY IT SHARES THE WORKSPACE'S localStorage RECORD ────────────────────────
 *
 * Before NAV-4 the workspace persisted and the section tab did not, so after a
 * global Back and a return the user landed back INSIDE their last open contact
 * with the section underneath silently reset to My Day. The restored thing and
 * the reset thing disagreed, which is what made it read as a glitch rather than
 * as a deliberate fresh start.
 *
 * Same record, same round-trip, so they cannot diverge again. That requires
 * exactly ONE writer: `persistToLocalStorage` receives `{...workspace,
 * sectionTab}` from CRMPage and serialises the whole record. Two writers against
 * one key would clobber each other — which is why there is no
 * `persistSectionTab` counterpart to this reader.
 *
 * ⚠ NO ID VALIDATION HERE, DELIBERATELY. The valid section-tab set is defined by
 * the `GROUPS` structure inside CRMPage — the thing that actually renders the
 * nav. (`TABS`, declared just above GROUPS in that file, is dead code left from
 * the grouped-nav refactor: nothing reads it, and the two have ALREADY drifted —
 * `catalog` is reachable in GROUPS and absent from TABS, so the "same 49 tab
 * keys" comment there is already untrue.) Duplicating that list here would be a
 * second source of truth that rots silently. This returns whatever string was
 * stored; CRMPage validates against the live GROUPS and falls back. A tab later
 * REMOVED from the nav therefore self-corrects rather than rendering a blank
 * pane.
 *
 * @returns {string|null} the stored id, or null when absent/malformed
 */
export function loadSectionTab(userId, storage) {
  const target = storage || (typeof window !== "undefined" ? window.localStorage : null);
  if (!target) return null;
  try {
    const raw = target.getItem(localStorageKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const v = parsed && parsed.sectionTab;
    if (typeof v !== "string") return null;
    const trimmed = v.trim();
    return trimmed.length > 0 ? trimmed : null;
  } catch (_) {
    return null;
  }
}

// Named exports above. No `module.exports` here — that's the line that
// tripped the webpack 5 strict-mode runtime check. See Task #94 header.
